import DatePicker from 'react-datepicker';
import events from 'lib/events';
import Loader from 'components/loader';
import moment from 'moment';
import Pagination from 'components/layout/pagination';
import PrintableReport from '../components/printableReport';
import PropTypes from 'prop-types';
import React, {Component} from 'react';
import ReportApi from 'api/report';
import ReportDropDown from '../components/reportDropDown';
import ReportTable from '../components/reportTable';
import {formatCurrency, formatStaticCurrency} from 'helpers/currencyHelper';
import {connectToPOSPermissions, POS_PERMISSIONS} from 'helpers/posPermissions';
import {FormattedMessage, intlShape, injectIntl} from 'react-intl';
import {reportDateFormat, salesProductCategory, salesProductCategoryFormat, dateTimeFormats} from 'helpers/constants';

const filterOptions = {
  store: '',
  storeOptions: [],
  productCategory: '',
  productCategoryOptions: [],
};

const tableMaxRows = 20;

class ProductSalesReport extends Component {
  static propTypes = {
    branches: PropTypes.any,
    intl: intlShape.isRequired,
  };

  constructor(props) {
    super(props);
    let {formatMessage} = this.props.intl;

    //Setup store filter
    filterOptions.store = '';
    filterOptions.storeOptions = [
      {value: '', label: formatMessage({id: 'general.all.stores'})},
    ];
    for (let i = 0; i < this.props.branchesInfo.length; i++) {
      filterOptions.storeOptions.push({
        value: this.props.branchesInfo[i].id, label: this.props.branchesInfo[i].name,
      });
    }

    //Setup category filter
    filterOptions.productCategory = '';
    filterOptions.productCategoryOptions = [
      {value: '', label: formatMessage({id: 'general.all'})},
    ];

    const categories = salesProductCategoryFormat(formatMessage);

    for (let key in categories) {
      if (categories.hasOwnProperty(key)) {
        filterOptions.productCategoryOptions.push({
          value: key, label: categories[key],
        });
      }
    }

    this.state = {
      loaded: true,
      summary: null,
      report: null,
      start: moment().subtract(30, 'days'),
      end: moment(),
      store: '',
      productCategory: filterOptions.productCategory,
      ...filterOptions,
      count: 0,
      total: 0,
      page: 1,
      showNoDataMessage: false,
    };
  }

  onPageChange = (page, event) => {
    const {count, fullReport} = this.state;
    const pagReport = fullReport.slice((page - 1) * count, count * page);

    this.setState({
      report: pagReport,
      page: page,
    });
  };

  exportReport = () => {
    const {start, end, store, productCategory} = this.state;

    ReportApi.getProductSalesReportExport(
      this.props.intl.locale,
      this.state.store,
      this.state.productCategory,
      moment(start).format('YYYY-MM-DD'),
      moment(end).format('YYYY-MM-DD')
    );
  };

  printReport = () => {
    const {fullReport, start, end} = this.state;
    let {formatMessage} = this.props.intl;

    const columns = [
      {name: formatMessage({id: 'general.product.code'}), key: 'sku'},
      {name: formatMessage({id: 'general.product.name'}), key: 'name'},
      {name: formatMessage({id: 'general.product.category'}), key: 'categoryName'},
      {name: formatMessage({id: 'general.average.price'}), key: 'avg_price_formatted'},
      {name: formatMessage({id: 'general.quantity.sold'}), key: 'quantity'},
      {name: formatMessage({id: 'general.total.sales'}), key: 'total_sales_formatted'},
    ];

    fullReport.forEach((current) => {
      current.categoryName = salesProductCategory[current.category];
      current.avg_price_formatted = formatStaticCurrency(current.avg_price);
      current.total_sales_formatted = formatStaticCurrency(current.total_sales);
    }, this);

    return (
      <PrintableReport
        report={fullReport}
        columns={columns}
        start={start.format(dateTimeFormats.numeralDate)}
        startLabel={formatMessage({id: 'general.start.date'})}
        end={end.format(dateTimeFormats.numeralDate)}
        endLabel={formatMessage({id: 'general.end.date'})}
        title={formatMessage({id: 'reports.product.sales'})}/>
    );
  };

  resetFilters = () => {
    this.setState({
      start: moment().subtract(30, 'days'),
      end: moment(),
      store: '',
      productCategory: filterOptions.productCategory,
    });
  };

  roundValue = val => {
    return parseFloat(Math.round(parseInt(val) * 100) / 100).toFixed(2);
  };

  updateReport = () => {
    const {start, end, store, productCategory, customerType} = this.state;

    this.setState({
      loaded: false,
    });

    ReportApi.getProductSalesReport(
      store,
      productCategory,
      moment(start).format('YYYY-MM-DD'),
      moment(end).format('YYYY-MM-DD')
    ).then(data => {
      this.setState({
        loaded: true,
        fullReport: data,
        report: data.slice(0, tableMaxRows),
        count: tableMaxRows,
        total: data.length,
        page: 1,
      });
      if (!Object.keys(data).length) {
        this.setState({showNoDataMessage: true});
      } else {
        this.setState({showNoDataMessage: false});
      }
    }).catch(err => {
      console.log(err);
      this.setState({showNoDataMessage: true});
    }).complete(() => {
      this.setState({
        loaded: true,
      });
    });
  };

  render() {
    const {
      start, end, store, storeOptions, productCategory, productCategoryOptions, summary, loaded, report, showNoDataMessage,
    } = this.state;
    let {formatMessage} = this.props.intl;

    return <div className='report'>
      <div className='row report-description no-padding'>
        <div className='col-sm-12'><FormattedMessage id="reports.product.sales.description"/></div>
      </div>
      <div className='row'>
        <div className='col-sm-12'>
          <div className='block-header'><FormattedMessage id="general.report.options"/></div>
        </div>
      </div>
      <div className='filter-section'>
        <div className='row'>
          <div className='col-sm-3'>
            <div className='form-group'>
              <label><FormattedMessage id="general.date.from"/></label>
              <div className='editable-datepicker'>
                <DatePicker
                  selected={start}
                  dateFormat={reportDateFormat}
                  maxDate={end.clone().subtract(1, 'days')}
                  onChange={((e) => {
                    this.setState({start: e});
                  })}
                />
              </div>
            </div>
          </div>

          <div className='col-sm-3'>
            <div className='form-group'>
              <label><FormattedMessage id="general.date.to"/></label>
              <div className='editable-datepicker'>
                <DatePicker
                  selected={end}
                  dateFormat={reportDateFormat}
                  minDate={start.clone()}
                  maxDate={moment()}
                  onChange={((e) => {
                    this.setState({end: e});
                  })}
                />
              </div>
            </div>
          </div>
        </div>

        <div className='row'>
          <div className='col-sm-3'>
            <ReportDropDown value={store}
                            label={formatMessage({id: 'general.store'})}
                            onChange={((e) => {
                              this.setState({store: e.target.value});
                            })}
                            options={storeOptions}/>
          </div>

          <div className='col-sm-3'>
            <ReportDropDown value={productCategory}
                            label={formatMessage({id: 'general.product.category'})}
                            onChange={((e) => {
                              this.setState({productCategory: e.target.value});
                            })}
                            options={productCategoryOptions}/>
          </div>
        </div>

        <div className="row">
          <div className="col-sm-12">
            <button className='btn btn-primary pull-right'
                    onClick={this.updateReport}><FormattedMessage id="reports.update.summary"/></button>
            <button className='btn text-button pull-right reset-button'
                    onClick={this.resetFilters}><FormattedMessage id="general.reset"/></button>
          </div>
        </div>
      </div>
      <Loader loaded={loaded}>
        {report ?
          <div>
            <div className='row'>
              <div className='col-sm-12 divider-line'>
                <h2 className="no-margin-bottom"><FormattedMessage id="reports.report.summary"/>
                  <span className="sub-date">{
                    ' | ' + formatMessage({id: 'general.date.range.labelled'}, {
                      start: moment(start).format(dateTimeFormats.numeralDate),
                      end: moment(end).format(dateTimeFormats.numeralDate),
                    })}
                  </span>
                </h2>
                <span><FormattedMessage id="reports.reveal.report.summary"/></span>
                {showNoDataMessage ?
                  <label className="report-no-data"><FormattedMessage id="reports.error.no.results"/></label> : ''}
              </div>
            </div>

            {!showNoDataMessage ?
              <div>
                <ReportTable data={report}
                             headers={[
                               {
                                 label: formatMessage({id: 'general.product.code'}), key: 'sku',
                                 style: {'textAlign': 'left'},
                               },
                               {
                                 label: formatMessage({id: 'general.product.name'}), key: 'name',
                                 style: {'textAlign': 'left'},
                               },
                               {
                                 label: formatMessage({id: 'general.product.category'}), key: 'category',
                                 style: {'textAlign': 'left'},
                                 format: i => salesProductCategory[i],
                               },
                               {
                                 label: formatMessage({id: 'general.average.price'}), key: 'avg_price',
                                 style: {'textAlign': 'right'},
                                 format: formatCurrency,
                               },
                               {
                                 label: formatMessage({id: 'general.quantity.sold'}), key: 'quantity',
                                 style: {'textAlign': 'right'},
                               },
                               {
                                 label: formatMessage({id: 'general.total.sales'}), key: 'total_sales',
                                 style: {'textAlign': 'right'},
                                 format: formatCurrency,
                               },
                             ]}/>
                <div className='center-pagination'>
                  <Pagination
                    onPageChange={this.onPageChange}
                    page={this.state.page}
                    count={this.state.count}
                    total={this.state.total}/>
                </div>
              </div> : ''
            }
          </div> : ''
        }

        <div className='col-sm-12 button-group divider-line'>
          <div className='row'>
            <div className='col-sm-12'>
              <button className='btn btn-primary pull-right'
                      onClick={this.exportReport}><FormattedMessage id="reports.export.report"/></button>
              <button className='btn btn-default pull-right'
                      disabled={(report === null)}
                      onClick={(() => {
                        events.publish('print', this.printReport());
                      })}><FormattedMessage id="reports.print.summary"/></button>
            </div>
          </div>
        </div>
      </Loader>
    </div>;
  }
}

export default injectIntl(connectToPOSPermissions(POS_PERMISSIONS.DETAILED_REPORTING)(ProductSalesReport));
